// Practice Projects Browse Page (Student View)
// Date: January 21, 2026

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { PracticeProjectCard } from '../../components/PracticeProjects';
import {
  usePracticeProjectsQuery,
  useEnrollPracticeProjectMutation,
} from '../../api/practiceProjectsApi';
import { PracticeProjectFilters as FiltersType, DifficultyLevel } from '../../types/practiceProjects';
import { BookOpen } from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';
import { ProjectFilters } from '../../components/ProjectFilters';

export default function PracticeProjectsBrowsePage() {
  // Filters state
  const [filters, setFilters] = useState<FiltersType>({
    search: '',
    difficulty_level: '',
    status: 'published', // Only show published projects to students
    tags: [],
    course_id: '',
    page: 1,
    limit: 12, // Grid layout - more items per page
  });

  // React Query hooks
  // Build API filters without empty values
  const apiFilters: any = {
    page: filters.page,
    limit: filters.limit,
  };
  if (filters.search) apiFilters.search = filters.search;
  if (filters.difficulty_level) apiFilters.difficulty_level = filters.difficulty_level;
  if (filters.status) apiFilters.status = filters.status;
  if (filters.tags.length > 0) apiFilters.tags = filters.tags;
  if (filters.course_id) apiFilters.course_id = filters.course_id;

  const { data, isLoading, isError, error } = usePracticeProjectsQuery(apiFilters);
  const enrollMutation = useEnrollPracticeProjectMutation();

  // Debug logging
  console.log('Browse Page - Loading:', isLoading);
  console.log('Browse Page - Error:', isError, error);
  console.log('Browse Page - Data:', data);

  // Handlers
  const handleEnroll = async (projectId: string) => {
    await enrollMutation.mutateAsync(projectId);
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Pagination
  const pagination = data?.data?.pagination;
  const projects = data?.data?.projects || [];

  console.log('Browse Page - Projects:', projects);
  console.log('Browse Page - Pagination:', pagination);

  return (
    <div className="px-6 pt-8 pb-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <BookOpen className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold">Practice Projects</h1>
        </div>
        <p className="text-muted-foreground text-base">
          Self-paced learning projects to build your skills. No deadlines, no grades—just
          hands-on practice.
        </p>
      </div>

      {/* Inline Horizontal Filters */}
      <div className="mb-6">
        <ProjectFilters
          searchQuery={filters.search}
          difficultyFilter={filters.difficulty_level}
          tags={filters.tags}
          onSearchChange={(value) => setFilters({ ...filters, search: value, page: 1 })}
          onDifficultyChange={(value) =>
            setFilters({ ...filters, difficulty_level: (value || '') as DifficultyLevel | '', page: 1 })
          }
          config={{
            showSearch: true,
            showDifficulty: true,
            showTags: false,
          }}
          searchPlaceholder="Search practice projects..."
        />
      </div>

      {/* Projects Grid */}
      <div>
          {isLoading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-80" />
              ))}
            </div>
          ) : isError ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No projects are available</h3>
              <p className="text-muted-foreground">
                Please check back later for new projects or try refreshing the page.
              </p>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No projects are available</h3>
              <p className="text-muted-foreground">
                Try adjusting your filters or check back later for new projects.
              </p>
            </div>
          ) : (
            <>
              {/* Project Count */}
              <div className="mb-6">
                <p className="text-sm text-muted-foreground">
                  Showing {projects.length} of {pagination?.totalItems || 0} projects
                </p>
              </div>

              {/* Projects Grid */}
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {projects.map((project) => (
                  <PracticeProjectCard
                    key={project.id}
                    project={project}
                    variant="browse"
                    onEnroll={handleEnroll}
                    showActions={true}
                  />
                ))}
              </div>
              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                  >
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {[...Array(pagination.totalPages)].map((_, index) => {
                      const page = index + 1;
                      // Show first page, last page, current page, and pages around current
                      const showPage =
                        page === 1 ||
                        page === pagination.totalPages ||
                        (page >= pagination.currentPage - 1 &&
                          page <= pagination.currentPage + 1);

                      if (!showPage) {
                        // Show ellipsis
                        if (
                          page === pagination.currentPage - 2 ||
                          page === pagination.currentPage + 2
                        ) {
                          return (
                            <span key={page} className="px-2">
                              ...
                            </span>
                          );
                        }
                        return null;
                      }

                      return (
                        <Button
                          key={page}
                          variant={page === pagination.currentPage ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
    </div>
  );
}
